import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Loading from '../../components/Loading';
import Pagination from '../../components/Pagination';
import { DEPARTMENTS, ROLES } from '../../utils/constants';
import api from '../../services/api';

const EmployeeManagement = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ department: '', role: '', is_active: '', search: '' });

  useEffect(() => {
    fetchEmployees();
    fetchStats();
  }, [page, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 10 });
      if (filters.department) params.append('department', filters.department);
      if (filters.role) params.append('role', filters.role);
      if (filters.is_active) params.append('is_active', filters.is_active);
      if (filters.search) params.append('search', filters.search);

      const response = await api.get(`/employees?${params}`);
      setEmployees(response.data.data.employees);
      setPagination(response.data.data.pagination);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/employees/stats');
      setStats(response.data.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
    setPage(1);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        await api.delete(`/employees/${id}`);
        fetchEmployees();
        fetchStats();
      } catch (error) {
        alert('Failed to delete employee: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await api.patch(`/employees/${id}`, { is_active: !currentStatus });
      fetchEmployees();
      fetchStats();
    } catch (error) {
      alert('Failed to update employee status: ' + (error.response?.data?.message || error.message));
    }
  };

  return (
    <Layout>
      <div className="container-fluid">
        <div className="dashboard-header">
          <h1>Employee Management</h1>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/admin/employees/new')}
          >
            <i className="ti ti-plus me-1" />
            Add Employee
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="stats-grid">
            <div className="stat-card">
              <h3>Total Employees</h3>
              <p className="stat-value">{stats.total}</p>
            </div>
            <div className="stat-card">
              <h3>Active</h3>
              <p className="stat-value text-success">{stats.active}</p>
            </div>
            <div className="stat-card">
              <h3>Inactive</h3>
              <p className="stat-value text-danger">{stats.inactive}</p>
            </div>
            {Object.entries(stats.by_department || {}).map(([dept, count]) => (
              <div key={dept} className="stat-card">
                <h3>{dept.replace(/_/g, ' ')}</h3>
                <p className="stat-value">{count}</p>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-sm-6 col-lg-3">
                <label className="form-label small mb-1">Department</label>
                <select
                  name="department"
                  className="form-select"
                  value={filters.department}
                  onChange={handleFilterChange}
                >
                  <option value="">All Departments</option>
                  {Object.values(DEPARTMENTS).map((dept) => (
                    <option key={dept} value={dept}>
                      {dept.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-sm-6 col-lg-3">
                <label className="form-label small mb-1">Role</label>
                <select
                  name="role"
                  className="form-select"
                  value={filters.role}
                  onChange={handleFilterChange}
                >
                  <option value="">All Roles</option>
                  {Object.values(ROLES).map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-sm-6 col-lg-3">
                <label className="form-label small mb-1">Status</label>
                <select
                  name="is_active"
                  className="form-select"
                  value={filters.is_active}
                  onChange={handleFilterChange}
                >
                  <option value="">All Status</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>

              <div className="col-sm-6 col-lg-3">
                <label className="form-label small mb-1">Search</label>
                <input
                  type="text"
                  name="search"
                  className="form-control"
                  placeholder="Search by name, email, or code"
                  value={filters.search}
                  onChange={handleFilterChange}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Employees Table */}
        <div className="card table-responsive">
          {loading ? (
            <Loading />
          ) : (
            <>
              <table className="table mb-0 text-nowrap table-hover">
                <thead className="table-light border-light">
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Employee Code</th>
                    <th>Departments</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-muted">
                        No employees found
                      </td>
                    </tr>
                  ) : (
                    employees.map((employee) => (
                      <tr key={employee._id} className="align-middle">
                        <td>{employee.name}</td>
                        <td>{employee.email}</td>
                        <td>{employee.employee_code}</td>
                        <td>
                          <div className="d-flex gap-1 flex-wrap">
                            {(employee.departments || [employee.department]).map((dept, idx) => (
                              <span key={idx} className="badge bg-primary text-capitalize" style={{ fontSize: '11px' }}>
                                {dept.replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="text-capitalize">{employee.role}</td>
                        <td>
                          <span className={`badge ${employee.is_active ? 'bg-success' : 'bg-danger'}`}>
                            {employee.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex gap-1 flex-wrap">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => navigate(`/admin/employees/${employee._id}`)}
                            >
                              Edit
                            </button>
                            <button
                              className={`btn btn-sm ${employee.is_active ? 'btn-outline-secondary' : 'btn-success'}`}
                              onClick={() => handleToggleStatus(employee._id, employee.is_active)}
                            >
                              {employee.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(employee._id)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div className="p-3">
                <Pagination pagination={pagination} onPageChange={setPage} />
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default EmployeeManagement;
